import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { t, Trans } from '@lingui/macro';

import BackgroundCheckCallout from '@rover/components/src/callouts/BackgroundCheckCallout';
import SearchResultCard from '@rover/components/src/cards/SearchResultCard';
import EXPERIMENTS from '@rover/constants/src/platform/experiments.constants';
import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Box, Flex, Heading } from '@rover/kibble/core';
import { BannerSeverity } from '@rover/kibble/patterns/Banner/BannerSeverity.constants';
import Toast from '@rover/kibble/patterns/Toast';
import useTheme from '@rover/kibble/themes/useTheme';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import {
  SearchBestMatchClickedEvent,
  SearchBestMatchResultsRenderedEvent,
  SearchCardClickedEvent,
} from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import BestMatchExplanationBottomSheet from '@rover/react-lib/src/pages/search/SearchPage/components/BottomSheets/BestMatchExplanationBottomSheet';
import { shouldShowMaximalSearchCard } from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import { getBookingFeeLearnMoreUrl } from '@rover/react-lib/src/utils/bookingFeeUtils';
import type { CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrls';
import type { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useExperimentsShim from '@rover/shared/js/hooks/useExperimentsShim';
import useIsBHUserShim from '@rover/shared/js/hooks/useIsBHUserShim';
import { SearchPageDaycareNoResults } from '@rover/shared/js/search/utilities/analyticsEvents';
import { getReferrer, referrerIsRover } from '@rover/shared/js/utils/referrer';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { Callout } from '@rover/types/src/Callout';
import { CalloutSlugs } from '@rover/types/src/Callout';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';
import {
  getIsPriceTransparency,
  getIsPriceTransparencyCondensed,
} from '@rover/utilities/priceTransparency';
import { getQueryParams } from '@rover/utilities/url';

import { usePetInfoCallout } from '../../utilities/usePetInfoCallout';
import { useSimplifiedFilters } from '../../utilities/useSimplifiedFilters';
import {
  BookingFeeLinkClickSearchEvent,
  BookingFeeLinkClickSeoEvent,
  PriceAnchoringCalloutViewed,
} from '../analyticsEvents';
import FeeExplanationBottomSheet from '../BottomSheets/FeeExplanationBottomSheet';
import PriceAnchoringCallout from '../Callouts/PriceAnchoringCallout';
import BestMatchExplanationModal from '../Modals/BestMatchExplanationModal';
import { FeeExplanationModal } from '../Modals/FeeExplanationModal';
import PtCondensedBanner from '../PtCondensedBanner/PtCondensedBanner';
import SearchSectionHeader, {
  HeaderScenariosEnum,
  searchHeaderBuilder,
} from '../SearchSectionHeader';
import SelectedProvider from '../SelectedProvider';

import AlternateSearchResults from './AlternateSearchResults';
import { EmptyResults, SearchError } from './Errors';
import MaximalSearchCardsGrid from './MaximalSearchCardsGrid';
import NoFacilitiesCallout from './NoFacilitiesCallout';
import { getCallout } from './utils';

export type Props = {
  alternateSearchResults: SearchResult[];
  alternateServiceName: string;
  alternateServiceDescription: string;
  bestMatchSearchResults?: SearchResult[];
  bookingFeePercentage?: string;
  countryConfigUrls?: CountryConfigurationUrls;
  bestMatchConfigs?: string[];
  currentFilters: SearchFilters;
  didFiltersChangeSincePageLoad: boolean;
  isBestMatchesSearch?: boolean;
  isBestMatchExperience?: boolean;
  hasBestMatches?: boolean;
  hoveringResultIndex?: number | null;
  isAuthenticated?: boolean | null;
  isMapExpanded: boolean;
  language: string;
  memberProfileLinkParams: Record<string, string | undefined>;
  priceTransparencyLevel?: PriceTransparencyLevel;
  searchErrorMessage: string;
  searchResults: SearchResult[];
  selectedProviderResult?: SearchResult;
  searchQueryDates: Record<'startDate' | 'endDate', Date | undefined>;
  searchQueryDistinctDates: { dates?: string };
  searchResponseNumberOfResults: number;
  isSeoPage: boolean;
  onFavoriteToggled: (personOpk: string) => void;
  updateFiltersAndFireSearchOnChange?: (filters: Partial<SearchFilters>) => void;
  onResultCardMouseEnter?: (index: number) => void;
  onResultCardMouseLeave?: () => void;
  searchCardRefs?: (instance: HTMLDivElement, index: number) => void;
  preferCatReviews: boolean;
  onClick?: (entityOpk: string, isFacility: boolean) => void;
  searchCallouts?: Callout[];
  isShowingPriceInCurrency?: boolean;
  cardTreatment: SearchCardTreatment;
};

const SearchResults: React.FC<Props> = ({
  alternateSearchResults,
  alternateServiceName,
  alternateServiceDescription,
  bestMatchConfigs = [],
  bestMatchSearchResults = [],
  bookingFeePercentage,
  countryConfigUrls,
  currentFilters,
  didFiltersChangeSincePageLoad,
  isBestMatchesSearch = false, // Whether or not the search query is filtering for best matches.
  isBestMatchExperience = false, // Whether or not the best matches headers should render.
  hasBestMatches = false, // Whether or not there are best matches to display. This helps with some logic relating to headers on subsequent pages.
  memberProfileLinkParams,
  priceTransparencyLevel,
  searchErrorMessage,
  searchResults,
  selectedProviderResult,
  searchQueryDates,
  searchQueryDistinctDates,
  isAuthenticated,
  isMapExpanded, // This controls the size of SearchResultsCard for viewports < 992px
  language,
  onFavoriteToggled,
  updateFiltersAndFireSearchOnChange,
  searchResponseNumberOfResults,
  onResultCardMouseEnter,
  onResultCardMouseLeave,
  hoveringResultIndex,
  searchCardRefs,
  isSeoPage,
  preferCatReviews,
  onClick = () => {},
  searchCallouts,
  isShowingPriceInCurrency = true,
  cardTreatment,
}) => {
  const theme = useTheme();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const [isBestMatchExplanationModalOpen, setIsBestMatchExplanationModalOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const { user } = useCurrentUserShim();
  const source = isMobileBrowser() ? 'mobile_web' : 'web';
  const personOpk = user?.opk ?? '';

  const searchErrorOccurred = searchErrorMessage !== '';
  const noSearchResults = !searchResults || searchResults.length === 0;
  const isPriceTransparency = getIsPriceTransparency(priceTransparencyLevel);
  const showMaximalSearchCard = shouldShowMaximalSearchCard(cardTreatment, isSeoPage);
  const {
    serviceType,
    atDaycareFacility: facilitiesIncludedFilter,
    dogCount,
    catCount,
    puppyCount,
    spacesRequired,
    page,
    pet,
    frequency,
  } = currentFilters;

  /**
   * Daycare facility related state
   */
  const experimentsShim = useExperimentsShim();
  const { isVariant } = experimentsShim || { isVariant: () => false };
  const [noFacilitiesCalloutDismissed, setNoFacilitiesCalloutDismissed] = useState(false);
  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);
  const [fireNoFacilitiesEvent, setFireNoFacilitiesEvent] = useState(true);
  const hasDaycareFacilityInResults = searchResults.some((result) => result.isFacility);
  const userInDaycareFacilityExperiment = isVariant(
    EXPERIMENTS.GINGR_DAYCARE_FILTER_OUTSIDE_MARKETS_EXPERIMENT
  );
  const userInBoardingFacilityExperiment = isVariant(
    EXPERIMENTS.GINGR_BOARDING_FILTER_OUTSIDE_MARKETS_EXPERIMENT
  );

  const currentFilterKeys = Object.keys(currentFilters);
  const initialQueryParams = useMemo(() => getQueryParams(), []);
  const isBrightHorizonsUser = useIsBHUserShim();

  const { isActive: isInSimplifiedMobileFilters } = useSimplifiedFilters();

  const { renderCalloutAtIndex, toastText, onToastClose } = usePetInfoCallout({
    callouts: searchCallouts || [],
    dogSet: { petIds: pet, dogCount: dogCount || 0, puppyCount: puppyCount || 0 },
    updateFiltersAndFireSearchOnChange,
  });

  useEffect(() => {
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    setPortalTarget(document.body);
  }, []);

  // Enhanced memberProfileLinkParams that includes pet counts for BH users
  const enhancedMemberProfileLinkParams = useMemo(() => {
    if (isBrightHorizonsUser) {
      return {
        ...memberProfileLinkParams,
        dogCount: dogCount?.toString(),
        catCount: catCount?.toString(),
        puppyCount: puppyCount?.toString(),
      };
    }
    return memberProfileLinkParams;
  }, [memberProfileLinkParams, isBrightHorizonsUser, dogCount, catCount, puppyCount]);

  const referrerIsUsHomepageSearch =
    referrerIsRover() &&
    ['/'].includes(getReferrer()?.pathname ?? '') &&
    Object.keys(initialQueryParams).some((key) => currentFilterKeys.includes(key)); // ensure initial query params are actual search filters

  const allTypes = currentFilters.atDaycareFacility && currentFilters.inSittersHome;

  // Don't show callout if "All Types" filter is selected and user did not search from the homepage
  const shouldHideForAllTypesFilter = allTypes && !referrerIsUsHomepageSearch;

  const displayNoFacilitiesCallout = [
    (userInDaycareFacilityExperiment && serviceType === ServiceType.DOGGY_DAY_CARE) ||
      (userInBoardingFacilityExperiment && serviceType === ServiceType.OVERNIGHT_BOARDING),
    facilitiesIncludedFilter,
    !hasDaycareFacilityInResults,
    !noSearchResults,
    !noFacilitiesCalloutDismissed,
    !shouldHideForAllTypesFilter,
  ].every(Boolean);

  if (displayNoFacilitiesCallout && fireNoFacilitiesEvent) {
    setFireNoFacilitiesEvent(false);
    emitAnalyticsEvent(new SearchPageDaycareNoResults());
  }
  // END Daycare facility related state

  const handleOpenBestMatchExplanationModal = (): void => {
    setIsBestMatchExplanationModalOpen(true);
    fireDataLayerEvent({
      event: 'best-matches-info-click',
      personId: personOpk,
    });
  };

  const alertLinkEvent = isSeoPage
    ? new BookingFeeLinkClickSeoEvent()
    : new BookingFeeLinkClickSearchEvent();

  const handlePriceAnchoringCalloutVisible = useCallback(() => {
    emitAnalyticsEvent(new PriceAnchoringCalloutViewed({ source, cardTreatment }));
  }, [cardTreatment, source]);

  const priceAnchoringCalloutRef = useOnIntersect(handlePriceAnchoringCalloutVisible, true, {
    threshold: 1.0,
  });

  /**
   * `onClick` is wrapped so we can build in data without having to prop-drill
   * into `SearchResultCard`s.
   */
  const wrappedOnClick = (
    entityOpk: string,
    isFacility: boolean,
    rank: number | null,
    isBestMatch?: boolean,
    maybeBestMatch?: boolean
  ): void => {
    // Previous logic inside `SelectedProvider` called this only if `isPriceTransparency` was false.
    if (!isPriceTransparency) {
      onClick(entityOpk, isFacility);
    }

    // Emit profile viewed metric with rank and cardTreatment
    const tags = {
      cardTreatment,
      source,
      rank: rank?.toString() ?? 'none',
    };
    emitAnalyticsEvent(new SearchCardClickedEvent(tags));

    if (isBestMatch || maybeBestMatch) {
      const bestMatchStatus = isBestMatch ? 'isBestMatch' : 'maybeBestMatch';
      emitAnalyticsEvent(
        new SearchBestMatchClickedEvent({
          bestMatchStatus,
          source,
          cardTreatment,
        })
      );
    }
  };

  const openFeeExplanationModal = (): void => {
    setIsFeeExplanationModalOpen(true);
  };

  useEffect(() => {
    if (bestMatchSearchResults.length > 0) {
      if (bestMatchConfigs.length > 0) {
        // Emit metrics when we have best match results, with each applicable config tagged.
        bestMatchConfigs.forEach((config) => {
          emitAnalyticsEvent(
            new SearchBestMatchResultsRenderedEvent({
              config,
              source,
              cardTreatment,
            })
          );
        });
      }
    }
  }, [bestMatchSearchResults.length, bestMatchConfigs, source, cardTreatment]);

  if (searchErrorOccurred) {
    return <SearchError />;
  }

  if (didFiltersChangeSincePageLoad) {
    setScreenReaderAnnouncement(
      t`Search results have changed due to your input.  There are now ${searchResponseNumberOfResults} results.`
    );
  }

  const emptyResultsCallout = getCallout(searchCallouts, CalloutSlugs.emptyResults);

  // If we have a full page of best matches, we don't want to show the empty results component.
  const emptyResultsComponent =
    noSearchResults && bestMatchSearchResults.length ? null : (
      <EmptyResults emptyResultCallout={emptyResultsCallout} />
    );

  const priceCallout = getCallout(searchCallouts, CalloutSlugs.priceAnchoring);

  const backgroundCheckCallout = getCallout(searchCallouts, CalloutSlugs.backgroundCheck);
  const isSearchInEurope = backgroundCheckCallout?.data?.badgeSlug === 'id-verified';

  const headers = searchHeaderBuilder({
    dates: searchQueryDates,
    bookingFeePercentage: bookingFeePercentage || '',
    frequency,
    hasBestMatches,
    hasResults: !noSearchResults,
    isAuthenticated: !!isAuthenticated,
    isBestMatchesSearch,
    isBestMatchExperience,
    isInitialPage: page === 1,
    language,
    petData: { dogCount, catCount, puppyCount, petCount: pet.length },
    priceTransparencyLevel: priceTransparencyLevel || PriceTransparencyLevels.none,
    selectedProvider: selectedProviderResult as OptimizedSearchResult,
    serviceType,
    spacesRequired,
    isSeoPage,
  });

  const selectedProviderHeader = headers[HeaderScenariosEnum.SELECTED_SITTER];
  const bestMatchHeader = headers[HeaderScenariosEnum.BEST_MATCHES];
  const mainResultsHeader = headers[HeaderScenariosEnum.SEARCH_RESULTS];

  const shouldDiplayPtMessage =
    bookingFeePercentage &&
    priceTransparencyLevel &&
    getIsPriceTransparencyCondensed(priceTransparencyLevel);

  const shouldHideBanner = isInSimplifiedMobileFilters && !isPriceTransparency;

  const renderCard = (index: number, searchResult: SearchResult): JSX.Element => (
    <SearchResultCard
      displayExpanded={!isMapExpanded}
      index={index}
      isActive={index === hoveringResultIndex}
      isAuthenticated={isAuthenticated}
      key={searchResult.personOpk}
      language={language}
      memberProfileLinkParams={enhancedMemberProfileLinkParams}
      onBlur={onResultCardMouseLeave}
      onFavoriteToggled={onFavoriteToggled}
      onFocus={onResultCardMouseEnter}
      onMouseEnter={onResultCardMouseEnter}
      onMouseLeave={onResultCardMouseLeave}
      searchResult={searchResult}
      // @ts-expect-error
      ref={searchCardRefs}
      isSeoPage={isSeoPage}
      preferCatReviews={preferCatReviews}
      onClick={wrappedOnClick}
      priceTransparencyLevel={priceTransparencyLevel}
      itemProp="provider"
      showFromText={!searchQueryDates.startDate || !searchQueryDates.endDate}
      isShowingPriceInCurrency={isShowingPriceInCurrency}
      isBHUser={isBrightHorizonsUser}
      cardTreatment={cardTreatment}
    />
  );

  const mainSearchResults = (): JSX.Element | JSX.Element[] => {
    const results = searchResults.map((searchResult: SearchResult, index: number) => (
      <React.Fragment key={searchResult.personOpk}>
        {priceCallout && index === priceCallout.position - 1 && (
          <Box
            borderBottom={
              showMaximalSearchCard ? undefined : `1px solid ${theme.colors.border.primary}`
            }
            sx={{
              gridColumn: showMaximalSearchCard ? '1 / -1' : 'unset',
            }}
            ref={priceAnchoringCalloutRef}
          >
            <PriceAnchoringCallout
              petDescription={priceCallout.data.petDescription}
              serviceUnit={priceCallout.data.serviceUnit}
              priceRange={priceCallout.data.priceRange}
              priceRangeAccessibilityLabel={priceCallout.data.priceRangeAccessibilityLabel}
            />
          </Box>
        )}
        {!!renderCalloutAtIndex(index) && (
          <Flex
            display="block"
            sx={{
              gridColumn: showMaximalSearchCard ? '1 / -1' : 'unset',
            }}
          >
            {renderCalloutAtIndex(index)}
          </Flex>
        )}
        {backgroundCheckCallout && index === backgroundCheckCallout.position && (
          <Box
            borderBottom={
              showMaximalSearchCard ? undefined : `1px solid ${theme.colors.border.primary}`
            }
            sx={{
              gridColumn: showMaximalSearchCard ? '1 / -1' : 'unset',
            }}
          >
            <BackgroundCheckCallout isInEurope={isSearchInEurope} isInline />
          </Box>
        )}
        {renderCard(index + bestMatchSearchResults.length, searchResult)}
      </React.Fragment>
    ));
    return showMaximalSearchCard ? <MaximalSearchCardsGrid results={results} /> : results;
  };

  const renderBestMatchResults = (): JSX.Element | JSX.Element[] => {
    const results = bestMatchSearchResults.map((searchResult: SearchResult, index: number) =>
      renderCard(index, searchResult)
    );
    return showMaximalSearchCard ? <MaximalSearchCardsGrid results={results} /> : results;
  };

  return (
    <>
      {toastText &&
        portalTarget &&
        createPortal(
          <Toast
            severity={BannerSeverity.SUCCESS}
            onClose={onToastClose}
            containerProps={{
              position: 'fixed',
              top: '5x',
              bottom: 'auto',
              left: '0x',
              right: '0x',
              zIndex: ZIndex.TOOLTIP.toString(),
              // @ts-expect-error margin shorthand not in Box types
              margin: '0 auto',
              sx: {
                transform: 'translateY(0)',
              },
            }}
          >
            {toastText}
          </Toast>,
          portalTarget
        )}
      <Heading webProps={{ as: 'h2', a11yHidden: true }}>
        <Trans>Search Results</Trans>
      </Heading>
      {!noSearchResults && getIsPriceTransparencyCondensed(priceTransparencyLevel) && (
        <Box position="sticky" top="0" zIndex={ZIndex.STICKY_HEADER.toString()}>
          <PtCondensedBanner
            priceTransparencyLevel={priceTransparencyLevel}
            onClick={openFeeExplanationModal}
            bookingFeePercentage={bookingFeePercentage}
            isSitterProfile={false}
          />
        </Box>
      )}

      {selectedProviderResult && selectedProviderHeader && (
        <>
          <SearchSectionHeader
            id={HeaderScenariosEnum.SELECTED_SITTER}
            headerIconName={selectedProviderHeader.icon}
            headerText={selectedProviderHeader.title}
            primarySubheaderText={selectedProviderHeader.primarySubheader}
            secondarySubheaderText={selectedProviderHeader.secondarySubheader}
            alertText={selectedProviderHeader.alertText}
            alertSeverity={selectedProviderHeader.alertSeverity}
            alertLinkHref={getBookingFeeLearnMoreUrl(countryConfigUrls)}
            alertLinkEvent={alertLinkEvent}
          />
          <SelectedProvider
            startDate={searchQueryDates.startDate}
            endDate={searchQueryDates.endDate}
            dates={searchQueryDistinctDates.dates}
            displayExpanded={!isMapExpanded}
            isAuthenticated={isAuthenticated}
            language={language}
            memberProfileLinkParams={enhancedMemberProfileLinkParams}
            onFavoriteToggled={onFavoriteToggled}
            searchResult={selectedProviderResult}
            // @ts-expect-error
            ref={searchCardRefs}
            isSeoPage={isSeoPage}
            preferCatReviews={preferCatReviews}
            priceTransparencyLevel={priceTransparencyLevel}
          />
        </>
      )}

      {bestMatchHeader && (
        <SearchSectionHeader
          id={HeaderScenariosEnum.BEST_MATCHES}
          headerIconName={bestMatchHeader.icon}
          headerText={bestMatchHeader.title}
          primarySubheaderText={bestMatchHeader.primarySubheader}
          secondarySubheaderText={bestMatchHeader.secondarySubheader}
          alertText={bestMatchHeader.alertText}
          alertSeverity={bestMatchHeader.alertSeverity}
          infoIconOnClick={handleOpenBestMatchExplanationModal}
          alertLinkHref={getBookingFeeLearnMoreUrl(countryConfigUrls)}
          alertLinkEvent={alertLinkEvent}
        />
      )}

      {bestMatchSearchResults.length > 0 && renderBestMatchResults()}

      {mainResultsHeader && (
        <Box display={[shouldHideBanner ? 'none' : 'block', 'block']}>
          <SearchSectionHeader
            id={HeaderScenariosEnum.SEARCH_RESULTS}
            headerIconName={mainResultsHeader.icon}
            headerText={mainResultsHeader.title}
            primarySubheaderText={mainResultsHeader.primarySubheader}
            secondarySubheaderText={mainResultsHeader.secondarySubheader}
            alertText={mainResultsHeader.alertText}
            alertSeverity={mainResultsHeader.alertSeverity}
            infoIconOnClick={
              mainResultsHeader.isBestMatchHeader ? handleOpenBestMatchExplanationModal : undefined
            }
            alertLinkHref={getBookingFeeLearnMoreUrl(countryConfigUrls)}
            alertLinkEvent={alertLinkEvent}
          />
        </Box>
      )}

      {displayNoFacilitiesCallout && (
        <Box p="6x" background="secondary">
          <NoFacilitiesCallout
            serviceType={serviceType}
            dismiss={() => setNoFacilitiesCalloutDismissed(true)}
          />
        </Box>
      )}

      {noSearchResults ? emptyResultsComponent : mainSearchResults()}

      {alternateSearchResults.length > 0 && (
        <AlternateSearchResults
          displayExpanded={!isMapExpanded}
          isAuthenticated={isAuthenticated}
          language={language}
          memberProfileLinkParams={memberProfileLinkParams}
          onFavoriteToggled={onFavoriteToggled}
          searchResults={alternateSearchResults}
          serviceName={alternateServiceName}
          serviceDescription={alternateServiceDescription}
          isSeoPage={isSeoPage}
          preferCatReviews={preferCatReviews}
          priceTransparencyLevel={priceTransparencyLevel}
          showFromText={!searchQueryDates.startDate || !searchQueryDates.endDate}
          isShowingPriceInCurrency={isShowingPriceInCurrency}
          cardTreatment={cardTreatment}
        />
      )}

      {source === 'mobile_web' ? (
        <BestMatchExplanationBottomSheet
          isOpen={isBestMatchExplanationModalOpen}
          onClose={() => setIsBestMatchExplanationModalOpen(false)}
        />
      ) : (
        <BestMatchExplanationModal
          isOpen={isBestMatchExplanationModalOpen}
          onClose={() => setIsBestMatchExplanationModalOpen(false)}
        />
      )}
      {source === 'mobile_web'
        ? shouldDiplayPtMessage && (
            <FeeExplanationBottomSheet
              isOpen={isFeeExplanationModalOpen}
              onClose={() => setIsFeeExplanationModalOpen(false)}
              priceTransparencyLevel={priceTransparencyLevel}
              bookingPercentageFee={bookingFeePercentage}
            />
          )
        : shouldDiplayPtMessage && (
            <FeeExplanationModal
              isOpen={isFeeExplanationModalOpen}
              onClose={() => setIsFeeExplanationModalOpen(false)}
              priceTransparencyLevel={priceTransparencyLevel}
              bookingPercentageFee={bookingFeePercentage}
            />
          )}
    </>
  );
};

export default SearchResults;
